import { isHtmlContent, isRichTextEmpty, plainTextToHtml } from './richText';

export type DescriptionSection = {
  id: string;
  title: string;
  content: string;
};

export type ParsedDescription = {
  intro: string;
  sections: DescriptionSection[];
};

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4']);

export function createDescriptionSection(title = ''): DescriptionSection {
  return {
    id: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    content: '',
  };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function parseDescription(html: string | null | undefined): ParsedDescription {
  const raw = (html || '').trim();
  if (!raw) return { intro: '', sections: [] };

  if (typeof DOMParser === 'undefined') {
    return { intro: raw, sections: [] };
  }

  const toParse = isHtmlContent(raw) ? raw : plainTextToHtml(raw);
  const doc = new DOMParser().parseFromString(`<div id="__root">${toParse}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return { intro: raw, sections: [] };

  const introParts: string[] = [];
  const sections: DescriptionSection[] = [];
  let current: DescriptionSection | null = null;

  const pushHtml = (chunk: string) => {
    if (current) current.content += chunk;
    else introParts.push(chunk);
  };

  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (HEADING_TAGS.has(el.tagName)) {
        current = {
          id: createDescriptionSection().id,
          title: (el.textContent || '').trim(),
          content: '',
        };
        sections.push(current);
        return;
      }
      pushHtml(el.outerHTML);
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim()) pushHtml(`<p>${escapeHtml(text)}</p>`);
    }
  });

  return {
    intro: introParts.join(''),
    sections,
  };
}

export function serializeDescription(parsed: ParsedDescription): string {
  const parts: string[] = [];
  if (parsed.intro && !isRichTextEmpty(parsed.intro)) {
    parts.push(parsed.intro);
  }
  parsed.sections.forEach((section) => {
    const title = (section.title || '').trim();
    const emptyContent = isRichTextEmpty(section.content);
    if (!title && emptyContent) return;
    if (title) parts.push(`<h2>${escapeHtml(title)}</h2>`);
    if (!emptyContent) parts.push(section.content);
  });
  return parts.join('');
}
